import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { selectCategoryById, useGetCategoriesQuery } from "@redux/features/categories/categoriesApi";
import { useAppSelector } from "@redux/hooks";

import BaseMaterialButton from "@reusables/BaseMaterialButton";
import RecursiveCategoriesTable
    from "@components/Dashboard/pages/SettingsPage/General/Categories/parts/RecursiveCategoriesTable";
import DeletionModal from "./modals/DeletionModal";

import { Can } from "@/casl.config";
import NoPermissionBanner from "@/components/ErrorPages/NoPermissionBanner";
import MutationModal from "@components/Dashboard/pages/SettingsPage/General/Categories/modals/MutationModal";

export default function CategoriesPage() {
    const { t } = useTranslation("", { keyPrefix: "settings.general.categories" });

    // Getting categories data using RTK Query
    const { data: categories = [], isLoading: isLoadingCategories } = useGetCategoriesQuery();

    // Open states determine visibility of modals
    const [isMutationModalOpen, setIsMutationModalOpen] = useState(false);
    const [isDeletionModalOpen, setIsDeletionModalOpen] = useState(false);

    // Ids and corresponding selected cache entries allows modals to work with data
    const [selectedCategoryId, setSelectedCategoryId] = useState<number>();
    const [deletableCategoryId, setDeletableCategoryId] = useState<number>();
    const [parentCategoryId, setParentCategoryId] = useState<number | null>(null);

    // Selected category and deletable category are used to pass data to modals
    const selectedCategory = useAppSelector((state) =>
        selectCategoryById(state, selectedCategoryId)
    );
    const deletableCategory = useAppSelector((state) =>
        selectCategoryById(state, deletableCategoryId)
    );

    return (
        <>
            <div className="levitation-extended">
                <div className="flex flex-row justify-between items-center mb-[44px]">
                    <div className="font-bold text-2xl text-accent">{t("heading")}</div>
                    <Can I="categories.edit" a="general">
                        <BaseMaterialButton fontWeight={500} onClick={() => {
                            setSelectedCategoryId(undefined);
                            setParentCategoryId(null);
                            setIsMutationModalOpen(true);
                        }}>
                            {t("table.buttons.add")}
                        </BaseMaterialButton>
                    </Can>
                </div>

                <Can not I="categories.view" a="general">
                    <NoPermissionBanner />
                </Can>

                <Can I="categories.view" a="general">
                    <RecursiveCategoriesTable
                        data={categories}
                        isDataLoading={isLoadingCategories}
                        onEdit={(category, parentId) => {
                            setSelectedCategoryId(category.id);
                            setParentCategoryId(parentId ?? null);
                            setIsMutationModalOpen(true);
                        }}
                        onCreateChild={(parentId) => {
                            setSelectedCategoryId(undefined);
                            setParentCategoryId(parentId);
                            setIsMutationModalOpen(true);
                        }}
                        onDelete={(category) => {
                            setDeletableCategoryId(category.id);
                            setIsDeletionModalOpen(true);
                        }}
                    />
                </Can>
            </div>

            <MutationModal
                isOpen={isMutationModalOpen}
                onClose={() => setIsMutationModalOpen(false)}
                category={selectedCategory}
                parentCategoryId={parentCategoryId}
            />

            <DeletionModal
                isOpen={isDeletionModalOpen}
                onClose={() => setIsDeletionModalOpen(false)}
                category={deletableCategory}
            />
        </>
    );
}
